import { enable as enableDarkMode, disable as disableDarkMode, DynamicThemeFix, setFetchMethod } from 'darkreader';
import { DeviceDetectionService } from '@libs/libraries/frontend';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Util } from '@libs/utilities/util';
import { EnumSystemTheme } from '@libs/constants';

import { EnumAddonDarkMode, IAddonDarkModeConfig } from './dark-mode.interface';
import { AddonDarkModeStorageHandler } from './dark-mode-storage.handler';
import { EnumAddonPageAddonInjection } from '../../shared/page-addons.interface';


@Injectable()
export class AddonDarkModeService {
	private static readonly THEME_TRANSITION_CLASS = 'ui-theme-transitioning';
	private static readonly THEME_TRANSITION_DURATION = 220;

	//** Helper Variables */
	private darkMode$: BehaviorSubject<EnumAddonDarkMode> = new BehaviorSubject<EnumAddonDarkMode>(EnumAddonDarkMode.Light);
	private themeTransitionTimer: number | null = null;
	isDarkMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);


	constructor(
		@Inject(EnumAddonPageAddonInjection.DarkModeConfig)
		private config					: IAddonDarkModeConfig,
		private darkModeStorageHandler	: AddonDarkModeStorageHandler,
		private deviceDetectionService	: DeviceDetectionService,
		private zone					: NgZone,
		@Inject(DOCUMENT) private document: Document
	) {
		//0 - get the saved mode value
		const savedMode: EnumAddonDarkMode | null = this.darkModeStorageHandler.getDarkMode();

		//1 - set it as initial value
		this.darkMode$ = new BehaviorSubject<EnumAddonDarkMode>(savedMode ?? EnumAddonDarkMode.Light);
		this.darkMode$.subscribe(() => {
			this.isDarkMode$.next(this.isDarkMode());
		});

		//2 - set the fetch
		setFetchMethod(window.fetch);
	}


	/**------------------------------------------------------
	 * Getter / State Helpers
	 */
	getActiveMode(): EnumAddonDarkMode {
		return this.darkMode$.getValue();
	}
	isLightMode(): boolean {
		return this.getActiveMode() === EnumAddonDarkMode.Light;
	}
	isDarkMode(): boolean {
		return !this.isLightMode();
	}


	/**------------------------------------------------------
	 * Subscribers
	 */
	onModeChange(callback: (mode: EnumAddonDarkMode) => void): AddonDarkModeService {
		this.darkMode$.subscribe((mode: EnumAddonDarkMode) => {
			callback(mode);
		});
		return this;
	}


	/**------------------------------------------------------
	 * Set Dark Mode
	 */
	setMode(mode: EnumAddonDarkMode): void {

		//0 - dark mode control for the library
		this.setModeInDarkReader(mode);

		//1 - set in active theme in the local storage
		this.darkModeStorageHandler.setDarkMode(mode);
		this.darkMode$.next(mode);
	}


	/**------------------------------------------------------
	 * System theme helper
	 */
	applySystemTheme(): void {

		//0 - check if system theme enabled
		if (!this.isAutoSystemThemeEnabled()) return;

		//1 - set the system theme
		if (this.deviceDetectionService.getSystemTheme() === EnumSystemTheme.Dark) this.setMode(EnumAddonDarkMode.Dark);
		else this.setMode(EnumAddonDarkMode.Light);
	}

	setSystemMode(): void {
		this.darkModeStorageHandler.setAutoSystemTheme(true);
		this.startThemeTransition();
		this.applySystemTheme();
	}

	resetAndDisableSystemTheme(): void {
		this.setMode(EnumAddonDarkMode.Light);
		this.disableAutoSystemTheme();
	}

	setCustomMode(mode: EnumAddonDarkMode): void {
		this.disableAutoSystemTheme();
		if (mode === this.getActiveMode()) return;

		this.startThemeTransition();
		this.setMode(mode);
	}

	isAutoSystemThemeEnabled(): boolean {
		return this.darkModeStorageHandler.getAutoSystemTheme();
	}

	isSystemThemeSet(): boolean {

		//0 - fetch the current active mode and theme
		const systemTheme: EnumSystemTheme = this.deviceDetectionService.getSystemTheme();
		const activeMode : EnumAddonDarkMode    = this.getActiveMode();

		//1 - check if the system theme is covered by the current mode
		const isSystemThemeSet: boolean = (systemTheme === EnumSystemTheme.Unknown)
			|| (systemTheme === EnumSystemTheme.Light && activeMode === EnumAddonDarkMode.Light)
			|| (systemTheme === EnumSystemTheme.Dark  && activeMode === EnumAddonDarkMode.Dark);

		return isSystemThemeSet;
	}

	private disableAutoSystemTheme() {
		this.darkModeStorageHandler.setAutoSystemTheme(false);
	}

	private startThemeTransition(): void {
		const view = this.document.defaultView;
		if (!view || view.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		const root = this.document.documentElement;
		root.classList.add(AddonDarkModeService.THEME_TRANSITION_CLASS);

		if (this.themeTransitionTimer !== null) view.clearTimeout(this.themeTransitionTimer);

		this.zone.runOutsideAngular(() => {
			this.themeTransitionTimer = view.setTimeout(() => {
				root.classList.remove(AddonDarkModeService.THEME_TRANSITION_CLASS);
				this.themeTransitionTimer = null;
			}, AddonDarkModeService.THEME_TRANSITION_DURATION);
		});
	}


	/**------------------------------------------------------
	 * Dark Mode Control for the Library
	 */
	private setModeInDarkReader(mode: EnumAddonDarkMode) {
		// Applications with a semantic native theme must not be transformed a
		// second time by DarkReader. Disabling it also removes generated styles
		// left behind when an application migrates to native tokens.
		if (this.config.NATIVE_THEME) {
			this.zone.runOutsideAngular(() => {
				disableDarkMode();
			});
			return;
		}

		//0 - create the dynamic fixes
		const dynamicFixes: DynamicThemeFix = {
			css						: this.config.CSS,
			ignoreInlineStyle		: this.config.IGNORE_INLINE_STYLE,
			ignoreImageAnalysis		: this.config.IGNORE_IMAGES,
			ignoreCSSUrl			: [],
			disableStyleSheetsProxy	: false,
			invert					: this.config.INVERT
		};

		//1 - set the dark mode
		this.zone.runOutsideAngular(() => {
			switch (mode) {
				case EnumAddonDarkMode.Light:
					disableDarkMode();
					break;

				case EnumAddonDarkMode.Dark:
					enableDarkMode({
						brightness	: 100,
						contrast	: 100,
						sepia		: 10
					}, dynamicFixes);
					break;

				default:
					throw new Error(`DarkModeService => setModeInDarkReader => FATAL ERROR: the mode of "${mode}" is not supported (supported values: "${Util.Enum.values(EnumAddonDarkMode)}")`);
			}
		});

		//2 - clear the console (to hide the errors for dark modes)
		if (mode !== EnumAddonDarkMode.Light) {
			setTimeout(() => Util.Log.clear(), 2000);
		}
	}
}
